import React from "react";
import { Badge, Dropdown, Table } from "@optimizely/axiom";
import { UserGroupWithInstance } from "../../../types/UserGroupWithInstance";
import { getFormattedDate } from "../../../lib/date-formatter";
import { MoreMenu } from "../MoreMenu/MoreMenu";
import { ATTRIBUTE_ROLES, GROUP_TYPES } from "../../../constants/constants";
import LimitByRole from "../LimitByRole/LimitByRole";
import { TableSkeleton } from "../Skeleton/TableSkeleton";

export const TeamsTable = ({
    onTeamClick,
    onTeamDelete,
    selectedTeam,
    teams,
    isLoading,
    showSCIMBadge = false,
    error
}: {
    onTeamClick: ({ team }: { team: UserGroupWithInstance }) => void;
    onTeamDelete: ({ team }: { team: UserGroupWithInstance }) => void;
    selectedTeam: UserGroupWithInstance | null;
    showSCIMBadge?: boolean;
    teams: UserGroupWithInstance[];
    isLoading: boolean;
    error: boolean;
}) => {
    if (isLoading) {
        return (
            <div className="push-double--top">
                <TableSkeleton fullWidth />
            </div>
        );
    }
    if (error) {
        return <div>Failed to load teams. Please try again later.</div>;
    }
    return (
        <Table density="loose">
            <Table.THead>
                <Table.TR>
                    <Table.TH sorting={{}} width="25%">
                        Team Name
                    </Table.TH>
                    <Table.TH sorting={{}} width="18%">
                        Product
                    </Table.TH>
                    <Table.TH sorting={{}} width="20%">
                        Instance
                    </Table.TH>
                    <Table.TH sorting={{}} width="10%">
                        Users
                    </Table.TH>
                    <Table.TH sorting={{}} width="12%">
                        Created
                    </Table.TH>
                    <Table.TH sorting={{}} width="10%">
                        Modified
                    </Table.TH>
                    <Table.TH sorting={{}} width="5%"></Table.TH>
                </Table.TR>
            </Table.THead>
            <Table.TBody>
                {teams.map((team) => {
                    const { created, id, instance, name, modified, groupType } = team;
                    return (
                        <Table.TR
                            key={id}
                            data-cy={`team-${team.id}-row`}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onTeamClick({ team });
                                }
                            }}
                            onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (
                                    target.closest('[data-cy="more-menu"]') ||
                                    target.closest('[data-cy="more-menu-contents"]')
                                ) {
                                    return;
                                }
                                onTeamClick({ team });
                            }}
                            backgroundColor={id === selectedTeam?.id ? "light" : "faint"}
                        >
                            <Table.TD className="link">
                                {name}{" "}
                                {showSCIMBadge && groupType === GROUP_TYPES.INTERNAL && (
                                    <Badge color="purple">SCIM</Badge>
                                )}
                            </Table.TD>
                            <Table.TD className="link">{instance?.productName ?? "-"}</Table.TD>
                            <Table.TD className="link">{instance?.nickname ?? instance?.name ?? "-"}</Table.TD>
                            <Table.TD>
                                <Badge>{team.userCount}</Badge>
                            </Table.TD>
                            <Table.TD>{getFormattedDate({ date: new Date(created), hideTimestamp: true })}</Table.TD>
                            <Table.TD>{getFormattedDate({ date: new Date(modified), hideTimestamp: true })}</Table.TD>
                            <Table.TD>
                                {/* SCIM teams cannot be deleted as they are managed by the SCIM provider */}
                                {groupType !== GROUP_TYPES.INTERNAL && (
                                    <LimitByRole mode="hide" action={ATTRIBUTE_ROLES.GROUPS.UPDATE}>
                                        <MoreMenu data-cy={`team-${team.id}-more-menu`}>
                                            <Dropdown.ListItem>
                                                <Dropdown.BlockLink
                                                    data-cy="delete-team-action"
                                                    onClick={() => onTeamDelete({ team })}
                                                >
                                                    <span className="danger">Delete team</span>
                                                </Dropdown.BlockLink>
                                            </Dropdown.ListItem>
                                        </MoreMenu>
                                    </LimitByRole>
                                )}
                            </Table.TD>
                        </Table.TR>
                    );
                })}
            </Table.TBody>
        </Table>
    );
};
